import { Subprocessors } from './Subprocessors';
import { ConfigProvider, Spin } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Home } from './Home';
import { ApiError, controlApi } from './control/api';
import type { Account } from './control/api';
import { AuthScreen, ControlPlane } from './control/ControlPlane';
import { RecoveryCancelPage } from './control/Recovery';
import { WaitPage } from './control/WaitPage';
import { lexTheme } from './theme';
import { antdLocaleFor, currentDisplayLanguage } from './i18n';
import { useTranslation } from 'react-i18next';
import { browserLanguages, displayLanguage, setServerLanguages } from './displayLanguage';
import type { LanguageFacts } from './control/api';

export type Mode = 'light' | 'dark';

function isControlPath(path: string): boolean {
  return path === '/signup' || path === '/login' || path === '/zones' || path.startsWith('/zones/') || path === '/control/recovery' || path === '/control/support/reports' || /^\/control\/support\/reports\/[a-f0-9-]{36}$/u.test(path);
}

/** The account's language from a session answer, its clock measured from the request's own timing (§3.2). */
function applyAccountLanguage(result: { readonly account: Account } & LanguageFacts, sentAt: number, receivedAt: number): void {
  if (result.supportedDisplayLanguages === undefined) return;
  if (result.serverNow !== undefined) displayLanguage.calibrate(result.serverNow, sentAt, receivedAt);
  setServerLanguages(result.supportedDisplayLanguages);
  void displayLanguage.signedIn({ browser: browserLanguages(), user: {
    id: result.account.id, displayLanguage: result.account.displayLanguage ?? null,
    displayLanguageChosenAt: result.account.displayLanguageChosenAt ?? 0 } });
}

export function Root() {
  const [mode, setMode] = useState<Mode>(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
  );

  // Drive the CSS custom-property theme (tokens.css) via data-theme on <html>.
  useEffect(() => {
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  const [path, setPath] = useState(window.location.pathname);
  const [returnPath, setReturnPath] = useState<string | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  // Which sign-in a session answer belongs to: sign-in, logout and a 401 each start a new one, so an answer that
  // arrives after them is dropped (i18n spec §3.2).
  const authGeneration = useRef(0);
  // Only the newest session answer is applied: an older one may name an account another tab has since replaced.
  const sessionRead = useRef(0);
  const accountId = useRef<string | null>(null);
  const [sessionLoading, setSessionLoading] = useState(() => isControlPath(window.location.pathname));
  const isControlRoute = isControlPath(path);
  const toggleTheme = useCallback(() => setMode((current) => current === 'light' ? 'dark' : 'light'), []);
  const navigate = useCallback((nextPath: string, replace = false) => {
    window.history[replace ? 'replaceState' : 'pushState'](null, '', nextPath);
    setPath(nextPath);
  }, []);

  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (!isControlRoute) return;
    let active = true;
    setSessionLoading(true);
    const generation = authGeneration.current;
    const read = ++sessionRead.current;
    const sentAt = Date.now();
    void controlApi.session()
      .then((result) => {
        if (!active || generation !== authGeneration.current || read !== sessionRead.current) return;
        // Signed in as someone else since this page last asked (another tab): start again as that account.
        if (accountId.current !== null && result.account.id !== accountId.current) {
          authGeneration.current += 1;
          window.location.reload();
          return;
        }
        setAccount(result.account);
        applyAccountLanguage(result, sentAt, Date.now());
      })
      .catch((error: unknown) => {
        // Only the newest read of the current sign-in may sign the page out: an older 401 says nothing about now.
        if (!active || generation !== authGeneration.current || read !== sessionRead.current) return;
        if (error instanceof ApiError && error.status === 401) { authGeneration.current += 1; setAccount(null); }
      })
      .finally(() => { if (active) setSessionLoading(false); });
    return () => { active = false; };
  }, [isControlRoute]);

  useEffect(() => {
    if (!sessionLoading && account !== null && (path === '/signup' || path === '/login')) navigate('/zones', true);
  }, [account, navigate, path, sessionLoading]);

  // Display language (doc/20261023-i18n-zh-cn.md §3.2): this device's choice or the browser at first paint; the
  // account's preference from each session answer while signed in. An older control plane reports no languages:
  // nothing is then saved.
  useEffect(() => { void displayLanguage.showSignedOut({ browser: browserLanguages() }); }, []);
  const signedIn = account !== null;
  useEffect(() => { accountId.current = account?.id ?? null; }, [account]);
  useEffect(() => { if (!signedIn) void displayLanguage.showSignedOut({ browser: browserLanguages() }); }, [signedIn]);

  /**
   * Reads the account's language again: after a sign-in (the verify answer lacks the facts) and whenever the page
   * regains focus, as another tab may have changed it. Only the newest answer of the current sign-in is applied; one
   * naming a different account reloads the page, so nothing of the previous account's survives into the next.
   */
  const refreshLanguage = useCallback(() => {
    const generation = authGeneration.current;
    const read = ++sessionRead.current;
    const sentAt = Date.now();
    void controlApi.session().then((result) => {
      if (generation !== authGeneration.current || read !== sessionRead.current) return;
      // Another tab signed in as someone else: the cookie is now that account's. Nothing this page holds or has in
      // flight belongs to it, so the page starts again as that account.
      if (accountId.current !== null && result.account.id !== accountId.current) {
        authGeneration.current += 1;
        window.location.reload();
        return;
      }
      applyAccountLanguage(result, sentAt, Date.now());
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!signedIn) return undefined;
    window.addEventListener('focus', refreshLanguage);
    return () => window.removeEventListener('focus', refreshLanguage);
  }, [signedIn, refreshLanguage]);

  const theme = useMemo(() => lexTheme(mode), [mode]);
  // Re-renders on a language change, so antd's built-in text follows the display language.
  useTranslation();
  const antdLocale = antdLocaleFor(currentDisplayLanguage());
  const onUnauthorized = useCallback(() => { authGeneration.current += 1; if (path.startsWith('/control/support/reports') || path === '/control/recovery') setReturnPath(path); setAccount(null); navigate('/login', true); }, [navigate, path]);
  const logout = useCallback(async () => {
    try { await controlApi.logout(); } finally { onUnauthorized(); }
  }, [onUnauthorized]);

  let content;
  if (path === '/subprocessors') {
    content = <Subprocessors />;
  } else if (path === '/wake') {
    // The wait page: no sign-in (doc/20261018-cloud-zone-browser.md §3.2.4). A pane on a sleeping zone gets the edge's
    // own pane asleep page instead (doc/20261021-cloud-addins.md §3.1).
    content = <WaitPage />;
  } else if (path === '/control/recovery/cancel') {
    // A recovery notice's cancel link: no sign-in (backup spec §4.6).
    content = <RecoveryCancelPage onOpenAccount={() => navigate('/control/recovery')} />;
  } else if (!isControlRoute) {
    content = <Home mode={mode} onToggleTheme={toggleTheme} />;
  } else if (sessionLoading) {
    content = <div className="cp-session-loading"><Spin size="large" /></div>;
  } else if (account === null) {
    content = <AuthScreen mode={mode} onToggleTheme={toggleTheme} onAuthenticated={(nextAccount) => { authGeneration.current += 1; setAccount(nextAccount); refreshLanguage(); navigate(returnPath ?? (path.startsWith('/control/support/reports') || path === '/control/recovery' ? path : '/zones'), true); setReturnPath(null); }} />;
  } else {
    content = <ControlPlane account={account} mode={mode} path={path} navigate={navigate} onToggleTheme={toggleTheme} onLogout={logout} onUnauthorized={onUnauthorized} />;
  }
  return <ConfigProvider theme={theme} locale={antdLocale}>{content}</ConfigProvider>;
}
